import { buildCompleteness, buildDocList, signAllDocs } from './consoleVerification';

// The verification file as one printable sheet.
//
// Replaces the share-link idea: instead of minting a token that opens the file to
// anyone holding the URL — with an expiry to manage, a revocation to remember and
// a view counter to watch — the reviewer saves a PDF and sends it to whoever needs
// it. Nothing new stays reachable on the internet afterwards.
//
// Printing rather than a PDF library is deliberate, and it is the pattern this
// codebase already uses for invoices (OrderInvoiceModal). Arabic and Chinese are
// the reason: jsPDF needs an embedded font and still will not shape Arabic
// correctly, while the browser shapes both perfectly and is already installed.
//
// Signed URLs expire in an hour. That is irrelevant once the sheet is saved — the
// images are rasterised into the PDF at print time — but it does mean the window
// must be printed rather than left open for the afternoon.

const esc = (v) => String(v === null || v === undefined ? '' : v)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const fmt = (v, isAr) => (v
  ? new Date(v).toLocaleDateString(isAr ? 'ar' : 'en', { year: 'numeric', month: 'short', day: 'numeric' })
  : '—');

const SHEET_CSS = `
  * { box-sizing: border-box; }
  body { margin: 0; padding: 26px; background: #fff; color: #1a1814;
         font-family: 'Tajawal', system-ui, -apple-system, 'Segoe UI', sans-serif; font-size: 13px; }
  h1 { font-size: 21px; margin: 0 0 2px; }
  .sub { color: rgba(0,0,0,.5); font-size: 12px; margin: 0 0 18px; }
  .sec { margin: 0 0 18px; border: 1px solid #e6e1d8; border-radius: 10px; padding: 14px 16px; }
  .sec > h2 { font-size: 12px; letter-spacing: .12em; text-transform: uppercase;
              color: rgba(0,0,0,.45); margin: 0 0 10px; font-weight: 600; }
  .kv { display: flex; gap: 10px; padding: 5px 0; border-bottom: 1px solid #f2efe9; }
  .kv:last-child { border-bottom: 0; }
  .kv .k { width: 40%; color: rgba(0,0,0,.5); }
  .kv .v { width: 60%; font-weight: 600; word-break: break-word; }
  .chips { display: flex; flex-wrap: wrap; gap: 6px; }
  .chip { border: 1px solid #e6e1d8; border-radius: 7px; padding: 4px 9px; font-size: 12px; }
  .chip.ok { border-color: #bfd8c8; background: #f1f7f3; }
  .chip.no { border-color: #efc9c6; background: #fdf1f0; color: #8E1F24; }
  .docs { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .doc { border: 1px solid #e6e1d8; border-radius: 10px; overflow: hidden; }
  .doc img { display: block; width: 100%; height: auto; }
  .doc .cap { padding: 7px 10px; font-size: 12px; border-top: 1px solid #f2efe9; }
  .doc.nofile .cap { border-top: 0; color: rgba(0,0,0,.5); }
  /* A page break inside a document card splits the image from its caption, which
     is how an unlabelled licence photo ends up in someone's inbox. */
  .doc, .sec { break-inside: avoid; page-break-inside: avoid; }
  @page { margin: 12mm; }
`;

/**
 * Open a print window for one supplier's verification file.
 * Returns false when the browser blocked the popup, so the caller can say so
 * rather than leaving the admin looking at nothing.
 */
export async function printVerificationFile(row, { isAr = true } = {}) {
  // Opened BEFORE the await: a popup opened after an async gap is no longer tied
  // to the click that asked for it, and Safari and Firefox both block it.
  const w = window.open('', '_blank');
  if (!w) return false;

  w.document.write(`<!doctype html><html dir="${isAr ? 'rtl' : 'ltr'}"><head><meta charset="utf-8">
    <title>${esc(row.company_name || row.full_name || 'Maabar')}</title></head>
    <body><p style="font-family:sans-serif;padding:24px">${isAr ? 'جارٍ التحضير…' : 'Preparing…'}</p></body></html>`);

  const docs = buildDocList(row);
  const completeness = row.completeness || buildCompleteness(row);
  // A long expiry on purpose: a slow printer must not race the signature.
  const urls = await signAllDocs(docs, 60 * 60);

  const kv = (k, v) => (v === null || v === undefined || String(v).trim() === ''
    ? ''
    : `<div class="kv"><div class="k">${esc(k)}</div><div class="v">${esc(v)}</div></div>`);

  const fieldChips = completeness.items.map((f) => `
    <span class="chip ${f.ok ? 'ok' : 'no'}">${f.ok ? '✓' : '○'} ${esc(isAr ? f.ar : f.en)}</span>`).join('');

  const docCards = docs.map((d) => {
    const label = esc(isAr ? d.ar : d.en);
    const url = urls[d.id];
    // Only images embed. A PDF or a video inside an <img> renders as a broken
    // icon, so they are named instead — an honest line beats a dead frame.
    if (d.kind === 'image' && url) {
      return `<div class="doc"><img src="${esc(url)}" alt="${label}"><div class="cap">${label}</div></div>`;
    }
    const kindWord = d.kind === 'pdf' ? 'PDF' : (isAr ? 'فيديو' : 'video');
    return `<div class="doc nofile"><div class="cap">${label} — ${esc(kindWord)}${
      isAr ? ' (غير مضمَّن في الطباعة)' : ' (not embedded in print)'}</div></div>`;
  }).join('');

  const html = `<!doctype html><html dir="${isAr ? 'rtl' : 'ltr'}"><head><meta charset="utf-8">
<title>${esc(row.company_name || row.full_name || 'Maabar')}</title>
<link href="https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;700&display=swap" rel="stylesheet">
<style>${SHEET_CSS}</style></head><body>
<h1>${esc(row.company_name || row.full_name || '—')}</h1>
<p class="sub">${esc(isAr ? 'ملف التوثيق — مَعبر' : 'Verification file — Maabar')}
  · ${esc(fmt(new Date().toISOString(), isAr))}</p>

<div class="sec"><h2>${isAr ? 'الحساب' : 'Account'}</h2>
  ${kv(isAr ? 'الحالة' : 'Status', row.status)}
  ${kv(isAr ? 'رقم المورّد' : 'Supplier ID', row.maabar_supplier_id)}
  ${kv(isAr ? 'تاريخ التسجيل' : 'Registered', fmt(row.created_at, isAr))}
  ${kv(isAr ? 'المصدر' : 'Origin', row.origin === 'factory_claim' ? (isAr ? 'مطالبة مصنع' : 'Factory claim') : (isAr ? 'تسجيل ذاتي' : 'Self-registered'))}
</div>

<div class="sec"><h2>${isAr ? 'اكتمال الملف' : 'File completeness'} — ${completeness.done}/${completeness.total}</h2>
  <div class="chips">${fieldChips}</div>
</div>

<div class="sec"><h2>${isAr ? 'بيانات الشركة' : 'Company'}</h2>
  ${kv(isAr ? 'رقم السجل' : 'Registration no.', row.reg_number || row.company_reg_number)}
  ${kv(isAr ? 'سنوات الخبرة' : 'Years of experience', row.years_experience)}
  ${kv(isAr ? 'عدد الموظفين' : 'Employees', row.num_employees)}
  ${kv(isAr ? 'نوع النشاط' : 'Business type', row.business_type)}
  ${kv(isAr ? 'التخصص' : 'Speciality', row.speciality)}
  ${kv(isAr ? 'المدينة / الدولة' : 'City / Country', [row.city, row.country].filter(Boolean).join(isAr ? '، ' : ', '))}
  ${kv(isAr ? 'الرابط التجاري' : 'Trade link', row.trade_link)}
  ${kv(isAr ? 'الموقع' : 'Website', row.company_website)}
  ${kv(isAr ? 'نبذة' : 'Description', row.company_description)}
</div>

<div class="sec"><h2>${isAr ? 'التواصل' : 'Contact'}</h2>
  ${kv(isAr ? 'البريد' : 'Email', row.email)}
  ${kv('WhatsApp', row.whatsapp)}
  ${kv('WeChat', row.wechat)}
  ${kv(isAr ? 'الهاتف' : 'Phone', row.phone)}
</div>

<div class="sec"><h2>${isAr ? 'الوثائق' : 'Documents'} — ${docs.length}</h2>
  ${docs.length ? `<div class="docs">${docCards}</div>`
    : `<p style="margin:0;color:rgba(0,0,0,.5)">${isAr ? 'لا توجد وثائق.' : 'No documents.'}</p>`}
</div>
</body></html>`;

  w.document.open();
  w.document.write(html);
  w.document.close();
  // Wait for the images, not a fixed delay: printing early gives blank frames
  // where the licence should be, and a fixed timeout is a guess about someone
  // else's connection.
  const go = () => { try { w.focus(); w.print(); } catch { /* the user closed it */ } };
  if (w.document.readyState === 'complete') setTimeout(go, 150);
  else w.addEventListener('load', () => setTimeout(go, 150), { once: true });
  return true;
}
